import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Mail, Play, CheckCircle2, XCircle, Sparkles, RefreshCw, Info } from "lucide-react";

const emptyForm = {
  email: "",
  app_password: "",
  imap_host: "imap.gmail.com",
  imap_port: 993,
  poll_minutes: 2,
  enabled: true,
  ai_filter: true,
};

export default function AdminGmail() {
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState({ configured: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [importing, setImporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/gmail");
      setStatus(data);
      if (data.configured) {
        setForm({
          email: data.email || "",
          app_password: "",
          imap_host: data.imap_host || "imap.gmail.com",
          imap_port: data.imap_port || 993,
          poll_minutes: data.poll_minutes || 2,
          enabled: data.enabled ?? true,
          ai_filter: data.ai_filter ?? true,
        });
      }
    } catch (err) {
      toast.error("Could not load Gmail settings", { description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const upd = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked
      : e.target.type === "number" ? Number(e.target.value)
      : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (status.configured && !payload.app_password) delete payload.app_password;
      const { data } = await api.put("/settings/gmail", payload);
      setStatus(data);
      toast.success("Gmail settings saved");
    } catch (err) {
      toast.error("Save failed", { description: formatApiError(err) });
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/settings/gmail/test");
      toast.success(`Connected — ${data.unread} unread in INBOX`);
    } catch (err) {
      toast.error("Test failed", { description: formatApiError(err) });
    } finally {
      setTesting(false);
    }
  };

  const pollNow = async () => {
    setPolling(true);
    try {
      const { data } = await api.post("/settings/gmail/poll-now");
      if (data.error) {
        toast.error("Poll failed", { description: data.error });
      } else {
        toast.success(`Poll done — created ${data.created}, discarded ${data.discarded}`);
        load();
      }
    } catch (err) {
      toast.error("Poll failed", { description: formatApiError(err) });
    } finally {
      setPolling(false);
    }
  };

  const importRecent = async () => {
    if (!window.confirm("Import the last 50 messages from your inbox (any read state), deduped by Message-Id?")) return;
    setImporting(true);
    try {
      const { data } = await api.post("/settings/gmail/import-recent?limit=50");
      if (data.error) {
        toast.error("Import failed", { description: data.error });
      } else {
        toast.success(
          `Imported — created ${data.created}, discarded ${data.discarded}, duplicates ${data.duplicates || 0}`
        );
        load();
      }
    } catch (err) {
      toast.error("Import failed", { description: formatApiError(err) });
    } finally {
      setImporting(false);
    }
  };

  if (loading) return <div className="p-10 text-center text-slate-500 text-sm">Loading Gmail settings...</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-display tracking-tight">
            Gmail intake
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Turn inbound Gmail messages into tickets automatically. Spam is filtered out by AI.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {status.configured && status.enabled && (
            <Button
              onClick={async () => {
                if (!window.confirm("Stop background polling immediately?")) return;
                try {
                  await api.post("/settings/gmail/stop");
                  toast.success("Polling stopped");
                  load();
                } catch (err) {
                  toast.error("Could not stop", { description: formatApiError(err) });
                }
              }}
              variant="outline"
              data-testid="gmail-stop-btn"
              className="border-rose-300 text-rose-700 hover:bg-rose-50 font-semibold"
            >
              Stop polling
            </Button>
          )}
          <div
            className={`beacon-badge ${
              status.configured && status.enabled
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-slate-100 text-slate-600 border-slate-200"
            }`}
            data-testid="gmail-status-badge"
          >
            {status.configured && status.enabled ? (
              <><CheckCircle2 className="w-3.5 h-3.5" /> Polling</>
            ) : status.configured ? (
              <><XCircle className="w-3.5 h-3.5" /> Disabled</>
            ) : (
              <>Not configured</>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-xl bg-blue-50/60 border border-blue-200 p-4 text-xs text-blue-900 flex gap-3 items-start">
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          Requires <span className="font-semibold">2-Step Verification</span> on the Gmail account, plus a{" "}
          <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline font-semibold">
            16-character App Password
          </a>. Only unread messages are processed — each one is marked read after intake.
        </div>
      </div>

      {status.password_unreadable && (
        <div data-testid="gmail-password-unreadable-alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Stored Gmail App Password could not be decrypted. Please re-enter it below and save.
        </div>
      )}

      <form onSubmit={save} className="beacon-card p-6 sm:p-7 space-y-5 shadow-sm">
        <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
          <Mail className="w-4 h-4 text-slate-500" />
          <div className="font-semibold text-slate-800 text-sm">Account</div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="beacon-label">Gmail address</label>
            <input required type="email" className="beacon-input" placeholder="support@yourcompany.com"
              data-testid="gmail-email-input"
              value={form.email} onChange={upd("email")} />
          </div>
          <div>
            <label className="beacon-label">
              App password {status.has_password && <span className="text-slate-400 font-normal">(leave blank to keep saved)</span>}
            </label>
            <input type="password" className="beacon-input font-mono"
              placeholder={status.has_password ? "•••••••• (unchanged)" : "16 characters, no spaces"}
              data-testid="gmail-password-input"
              value={form.app_password} onChange={upd("app_password")} />
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className="beacon-label">IMAP host</label>
            <input className="beacon-input" data-testid="gmail-host-input"
              value={form.imap_host} onChange={upd("imap_host")} />
          </div>
          <div>
            <label className="beacon-label">Port</label>
            <input type="number" className="beacon-input" data-testid="gmail-port-input"
              value={form.imap_port} onChange={upd("imap_port")} />
          </div>
          <div>
            <label className="beacon-label">Poll every (minutes)</label>
            <input type="number" min={1} max={60} className="beacon-input" data-testid="gmail-poll-minutes-input"
              value={form.poll_minutes} onChange={upd("poll_minutes")} />
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" data-testid="gmail-enabled-toggle"
              checked={form.enabled} onChange={upd("enabled")}
              className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
            Enable background polling
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" data-testid="gmail-ai-filter-toggle"
              checked={form.ai_filter} onChange={upd("ai_filter")}
              className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
            AI spam filter (Claude)
          </label>
        </div>
        <p className="text-[11px] text-slate-500 -mt-2">
          Turn AI filter <span className="font-semibold">off</span> to create a ticket for every unread email — useful if the classifier is over-discarding.
        </p>

        <div className="flex items-center justify-between pt-2 flex-wrap gap-3">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Sparkles className="w-3.5 h-3.5 text-blue-600" />
            Each fetched email is passed through Claude Sonnet 4.6 — only genuine support requests become tickets.
          </div>
          <Button type="submit" disabled={saving} data-testid="gmail-save-btn"
            className="bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-lg">
            {saving ? "Saving..." : "Save settings"}
          </Button>
        </div>
      </form>

      <div className="beacon-card p-6 shadow-sm space-y-4">
        <div className="flex items-center gap-2">
          <Play className="w-4 h-4 text-slate-500" />
          <div className="font-semibold text-slate-800 text-sm">Test & run</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={test} disabled={testing || !status.configured}
            variant="outline" data-testid="gmail-test-btn">
            {testing ? "Testing..." : "Test connection"}
          </Button>
          <Button onClick={pollNow} disabled={polling || !status.configured}
            data-testid="gmail-poll-now-btn"
            className="bg-blue-600 hover:bg-blue-700 text-white">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            {polling ? "Polling..." : "Poll unread now"}
          </Button>
          <Button onClick={importRecent} disabled={importing || !status.configured}
            variant="outline" data-testid="gmail-import-recent-btn">
            {importing ? "Importing..." : "Import last 50 (any state)"}
          </Button>
        </div>
        <p className="text-[11px] text-slate-500">
          <span className="font-semibold">Poll unread</span> processes only new emails and marks them read.
          <span className="font-semibold"> Import last 50</span> pulls recent messages regardless of read status, dedupes by Message-Id, and does <span className="font-semibold">not</span> mark them read — perfect if your inbox emails were already opened.
        </p>

        {status.last_run_at && (
          <div className="text-xs text-slate-500 space-y-1 pt-2 border-t border-slate-100">
            <div>Last run: <span className="font-mono">{new Date(status.last_run_at).toLocaleString()}</span></div>
            {status.last_result && (
              <div data-testid="gmail-last-result">
                Mode <span className="font-mono">{status.last_result.mode || "unseen"}</span> ·
                Fetched <span className="font-semibold text-slate-800">{status.last_result.fetched}</span>,
                created <span className="font-semibold text-emerald-700">{status.last_result.created}</span>,
                discarded <span className="font-semibold text-slate-600">{status.last_result.discarded}</span>
                {typeof status.last_result.duplicates === "number" && (
                  <>, duplicates <span className="font-semibold text-slate-500">{status.last_result.duplicates}</span></>
                )}
                {typeof status.last_result.loop_blocked === "number" && status.last_result.loop_blocked > 0 && (
                  <>, loop-blocked <span className="font-semibold text-rose-600">{status.last_result.loop_blocked}</span></>
                )}.
              </div>
            )}
            {status.last_error && (
              <div className="text-rose-700 mt-1" data-testid="gmail-last-error">
                Last error: {status.last_error}
              </div>
            )}
          </div>
        )}

        {status.last_result?.decisions?.length > 0 && (
          <div className="pt-3 border-t border-slate-100">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">
              Last {status.last_result.decisions.length} decisions
            </div>
            <ul className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="gmail-decisions-list">
              {status.last_result.decisions.map((d, i) => (
                <li key={i} className={`flex items-start gap-2 text-xs rounded-lg border p-2 ${
                  d.is_support ? "bg-emerald-50/60 border-emerald-200" : "bg-slate-50 border-slate-200"
                }`} data-testid={`gmail-decision-${i}`}>
                  <span className={`shrink-0 mt-0.5 w-4 h-4 rounded grid place-items-center text-[9px] font-bold ${
                    d.is_support ? "bg-emerald-600 text-white" : "bg-slate-400 text-white"
                  }`}>
                    {d.is_support ? "✓" : "×"}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-slate-800 font-semibold truncate">{d.subject || "(no subject)"}</div>
                    <div className="text-slate-500 truncate">
                      <span className="font-mono">{d.from}</span>
                      {d.reason && <> · <span className="italic">{d.reason}</span></>}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
